import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import DOMPurify from 'dompurify';

import './EnhancedRichTextEditor.css';

const EnhancedRichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = "Enter content...",
  editorType = 'quill', // 'quill', 'tiptap', 'slate'
  height = '300px',
  onHtmlImported // New prop to notify parent when HTML is imported
}) => {
  const [editorTypeState, setEditorTypeState] = useState(editorType);
  const [importStatus, setImportStatus] = useState('');
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  // React Quill Configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [
        'Arial',
        'Times New Roman', 
        'Courier New',
        'Georgia',
        'Verdana',
        'Helvetica',
        'Playfair Display',
        'Merriweather',
        'Lora',
        'Roboto',
        'Open Sans',
        'Source Sans Pro',
        'Montserrat',
        'Raleway',
        'Poppins',
        'Dancing Script',
        'Great Vibes',
        'Pacifico',
        'Satisfy',
        'Kaushan Script',
        'Allura',
        'Alex Brush',
        'Tangerine',
        'Sacramento',
        'Crimson Text',
        'Libre Baskerville',
        'Cormorant Garamond',
        'EB Garamond',
        'Bodoni Moda',
        'Cormorant',
        'Abril Fatface',
        'Bebas Neue',
        'Oswald',
        'Josefin Sans',
        'Quicksand',
        'Nunito',
        'Work Sans',
        'Inter',
        'Ubuntu',
        'Fira Sans',
        'Noto Sans',
        'Roboto Condensed'
      ]}],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean'],
      [{ 'table': [] }],
      [{ 'formula': [] }],
      [{ 'emoji': [] }]
    ],
  };

  // Handle HTML file import
  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is HTML
    if (!file.name.match(/\.(html|htm)$/i)) {
      setImportStatus('Error: Please select an HTML file (.html or .htm)');
      setTimeout(() => setImportStatus(''), 3000);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const htmlContent = e.target.result;
        
        // Sanitize HTML content using DOMPurify
        const sanitizedHTML = DOMPurify.sanitize(htmlContent, {
          ALLOWED_TAGS: [
            'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'div', 'span', 'a', 'img', 'table',
            'thead', 'tbody', 'tr', 'td', 'th', 'hr'
          ],
          ALLOWED_ATTR: [
            'href', 'src', 'alt', 'title', 'class', 'style', 'width', 'height', 'target'
          ],
          ALLOW_DATA_ATTR: false
        });

        // Insert sanitized content into editor
        if (editorTypeState === 'quill' && quillRef.current) {
          try {
            const quill = quillRef.current.getEditor();
            if (quill) {
              // Get current selection
              const range = quill.getSelection();
              if (range && range.length > 0) {
                // Insert at cursor position
                quill.insertEmbed(range.index, 'div', sanitizedHTML);
              } else {
                // Replace entire content
                quill.clipboard.dangerouslyPasteHTML(sanitizedHTML);
              }
              // Trigger onChange to update parent component
              onChange(quill.root.innerHTML);
            }
          } catch (quillError) {
            console.error('Error inserting content into Quill:', quillError);
            setImportStatus('Error: Failed to insert content into editor');
            setTimeout(() => setImportStatus(''), 3000);
            return;
          }
        } else if (editorTypeState === 'tiptap' && tiptapEditor) {
          try {
            tiptapEditor.commands.setContent(sanitizedHTML);
            // Trigger onChange to update parent component
            onChange(sanitizedHTML);
          } catch (tiptapError) {
            console.error('Error inserting content into TipTap:', tiptapError);
            setImportStatus('Error: Failed to insert content into editor');
            setTimeout(() => setImportStatus(''), 3000);
            return;
          }
        }

        // Notify parent component that HTML was imported
        if (onHtmlImported) {
          onHtmlImported(true);
        }

        setImportStatus('HTML content imported successfully!');
        setTimeout(() => setImportStatus(''), 3000);
        
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error processing HTML file:', error);
        setImportStatus('Error: Failed to process HTML file');
        setTimeout(() => setImportStatus(''), 3000);
      }
    };

    reader.onerror = () => {
      setImportStatus('Error: Failed to read file');
      setTimeout(() => setImportStatus(''), 3000);
    };

    reader.readAsText(file);
  };

  // Configure Quill to recognize custom fonts
  useEffect(() => {
    if (window.Quill) {
      // Import and completely override the Font format
      const Font = window.Quill.import('formats/font');
      
      // Define our custom fonts - completely replace Quill's defaults
      const customFonts = [
        'Arial',
        'Times New Roman', 
        'Courier New',
        'Georgia',
        'Verdana',
        'Helvetica',
        'Playfair Display',
        'Merriweather',
        'Lora',
        'Roboto',
        'Open Sans',
        'Source Sans Pro',
        'Montserrat',
        'Raleway',
        'Poppins',
        'Dancing Script',
        'Great Vibes',
        'Pacifico',
        'Satisfy',
        'Kaushan Script',
        'Allura',
        'Alex Brush',
        'Tangerine',
        'Sacramento',
        'Crimson Text',
        'Libre Baskerville',
        'Cormorant Garamond',
        'EB Garamond',
        'Bodoni Moda',
        'Cormorant',
        'Abril Fatface',
        'Bebas Neue',
        'Oswald',
        'Josefin Sans',
        'Quicksand',
        'Nunito',
        'Work Sans',
        'Inter',
        'Ubuntu',
        'Fira Sans',
        'Noto Sans',
        'Roboto Condensed'
      ];
      
      // Completely replace the font whitelist
      Font.whitelist = customFonts;
      
      // Override the font format's create method to prevent default fonts
      const originalCreate = Font.create;
      Font.create = function(value) {
        // Only allow our custom fonts
        if (customFonts.includes(value)) {
          return originalCreate.call(this, value);
        }
        return null;
      };
      
      // Re-register the modified Font format
      window.Quill.register(Font, true);
      
      // Override the font picker to ensure proper display and prevent duplicates
      const Picker = window.Quill.import('ui/picker');
      if (Picker) {
        const originalBuildOptions = Picker.prototype.buildOptions;
        Picker.prototype.buildOptions = function() {
          // Clear existing options
          this.options.innerHTML = '';
          
          // Add only our custom fonts
          customFonts.forEach(font => {
            const item = document.createElement('span');
            item.setAttribute('data-value', font);
            item.textContent = font;
            item.style.fontFamily = font;
            item.className = 'ql-picker-item';
            this.options.appendChild(item);
          });
        };
        
        // Override buildLabel to ensure proper font display
        Picker.prototype.buildLabel = function(item) {
          const label = document.createElement('span');
          const fontValue = item.getAttribute('data-value');
          label.textContent = fontValue;
          label.style.fontFamily = fontValue;
          return label;
        };
      }
      
      // Add comprehensive CSS to ensure proper font display and scrollability
      const style = document.createElement('style');
      style.textContent = `
        /* Make font dropdown scrollable */
        .ql-toolbar .ql-font .ql-picker-options {
          max-height: 200px !important;
          overflow-y: auto !important;
          scrollbar-width: thin !important;
          scrollbar-color: #c1c1c1 #f1f1f1 !important;
        }
        
        /* Scrollbar styling for webkit browsers */
        .ql-toolbar .ql-font .ql-picker-options::-webkit-scrollbar {
          width: 8px !important;
        }
        
        .ql-toolbar .ql-font .ql-picker-options::-webkit-scrollbar-track {
          background: #f1f1f1 !important;
          border-radius: 4px !important;
        }
        
        .ql-toolbar .ql-font .ql-picker-options::-webkit-scrollbar-thumb {
          background: #c1c1c1 !important;
          border-radius: 4px !important;
        }
        
        .ql-toolbar .ql-font .ql-picker-options::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8 !important;
        }
        
        /* Ensure each font option displays correctly */
        .ql-toolbar .ql-font .ql-picker-item {
          padding: 8px 12px !important;
          font-size: 14px !important;
          line-height: 1.4 !important;
          border-bottom: 1px solid #f0f0f0 !important;
          cursor: pointer !important;
          transition: background-color 0.2s ease !important;
        }
        
        .ql-toolbar .ql-font .ql-picker-item:hover {
          background-color: #f8f9fa !important;
        }
        
        .ql-toolbar .ql-font .ql-picker-item.ql-selected {
          background-color: #e3f2fd !important;
          color: #1976d2 !important;
        }
        
        /* Force font display for each option */
        .ql-toolbar .ql-font .ql-picker-item[data-value="Arial"] { font-family: Arial, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Times New Roman"] { font-family: "Times New Roman", serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Courier New"] { font-family: "Courier New", monospace !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Georgia"] { font-family: Georgia, serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Verdana"] { font-family: Verdana, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Helvetica"] { font-family: Helvetica, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Playfair Display"] { font-family: "Playfair Display", serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Merriweather"] { font-family: Merriweather, serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Lora"] { font-family: Lora, serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Roboto"] { font-family: Roboto, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Open Sans"] { font-family: "Open Sans", sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Source Sans Pro"] { font-family: "Source Sans Pro", sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Montserrat"] { font-family: Montserrat, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Raleway"] { font-family: Raleway, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Poppins"] { font-family: Poppins, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Dancing Script"] { font-family: "Dancing Script", cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Great Vibes"] { font-family: "Great Vibes", cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Pacifico"] { font-family: Pacifico, cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Satisfy"] { font-family: Satisfy, cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Kaushan Script"] { font-family: "Kaushan Script", cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Allura"] { font-family: Allura, cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Alex Brush"] { font-family: "Alex Brush", cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Tangerine"] { font-family: Tangerine, cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Sacramento"] { font-family: Sacramento, cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Crimson Text"] { font-family: "Crimson Text", serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Libre Baskerville"] { font-family: "Libre Baskerville", serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Cormorant Garamond"] { font-family: "Cormorant Garamond", serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="EB Garamond"] { font-family: "EB Garamond", serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Bodoni Moda"] { font-family: "Bodoni Moda", serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Cormorant"] { font-family: Cormorant, serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Abril Fatface"] { font-family: "Abril Fatface", cursive !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Bebas Neue"] { font-family: "Bebas Neue", sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Oswald"] { font-family: Oswald, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Josefin Sans"] { font-family: "Josefin Sans", sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Quicksand"] { font-family: Quicksand, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Nunito"] { font-family: Nunito, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Work Sans"] { font-family: "Work Sans", sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Inter"] { font-family: Inter, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Ubuntu"] { font-family: Ubuntu, sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Fira Sans"] { font-family: "Fira Sans", sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Noto Sans"] { font-family: "Noto Sans", sans-serif !important; }
        .ql-toolbar .ql-font .ql-picker-item[data-value="Roboto Condensed"] { font-family: "Roboto Condensed", sans-serif !important; }
        
        /* Hide any default Quill font options that might still appear */
        .ql-toolbar .ql-font .ql-picker-item[data-value="sans-serif"],
        .ql-toolbar .ql-font .ql-picker-item[data-value="serif"],
        .ql-toolbar .ql-font .ql-picker-item[data-value="monospace"] {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'script',
    'blockquote', 'code-block',
    'list', 'bullet', 'check', 'indent',
    'direction', 'align',
    'link', 'image', 'video',
    'table', 'formula', 'emoji'
  ];

  // TipTap Configuration
  const tiptapEditor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'custom-link',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'custom-image',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Color,
      TextStyle,
      Highlight.configure({
        multicolor: true,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const renderEditor = () => {
    switch (editorTypeState) {
      case 'tiptap':
                 return (
           <div className="tiptap-editor">
             <div className="tiptap-toolbar">
               <select
                 onChange={(e) => {
                   const font = e.target.value;
                   if (font !== 'default') {
                     tiptapEditor.chain().focus().setFontFamily(font).run();
                   }
                 }}
                 className="font-selector"
                 title="Font Family"
               >
                 <option value="default">Font</option>
                 <option value="Arial">Arial</option>
                 <option value="Times New Roman">Times New Roman</option>
                 <option value="Georgia">Georgia</option>
                 <option value="Playfair Display">Playfair Display</option>
                 <option value="Merriweather">Merriweather</option>
                 <option value="Lora">Lora</option>
                 <option value="Roboto">Roboto</option>
                 <option value="Open Sans">Open Sans</option>
                 <option value="Montserrat">Montserrat</option>
                 <option value="Raleway">Raleway</option>
                 <option value="Poppins">Poppins</option>
                 <option value="Dancing Script">Dancing Script</option>
                 <option value="Great Vibes">Great Vibes</option>
                 <option value="Pacifico">Pacifico</option>
                 <option value="Satisfy">Satisfy</option>
                 <option value="Kaushan Script">Kaushan Script</option>
                 <option value="Allura">Allura</option>
                 <option value="Alex Brush">Alex Brush</option>
                 <option value="Tangerine">Tangerine</option>
                 <option value="Sacramento">Sacramento</option>
                 <option value="Crimson Text">Crimson Text</option>
                 <option value="Libre Baskerville">Libre Baskerville</option>
                 <option value="Cormorant Garamond">Cormorant Garamond</option>
                 <option value="EB Garamond">EB Garamond</option>
                 <option value="Bodoni Moda">Bodoni Moda</option>
                 <option value="Abril Fatface">Abril Fatface</option>
                 <option value="Bebas Neue">Bebas Neue</option>
                 <option value="Oswald">Oswald</option>
                 <option value="Josefin Sans">Josefin Sans</option>
                 <option value="Quicksand">Quicksand</option>
                 <option value="Nunito">Nunito</option>
                 <option value="Work Sans">Work Sans</option>
                 <option value="Inter">Inter</option>
                 <option value="Ubuntu">Ubuntu</option>
               </select>
               <div className="toolbar-divider"></div>
               <button
                 onClick={() => tiptapEditor.chain().focus().toggleBold().run()}
                 className={tiptapEditor.isActive('bold') ? 'is-active' : ''}
                 title="Bold"
               >
                 <strong>B</strong>
               </button>
              <button
                onClick={() => tiptapEditor.chain().focus().toggleItalic().run()}
                className={tiptapEditor.isActive('italic') ? 'is-active' : ''}
                title="Italic"
              >
                <em>I</em>
              </button>
              <button
                onClick={() => tiptapEditor.chain().focus().toggleUnderline().run()}
                className={tiptapEditor.isActive('underline') ? 'is-active' : ''}
                title="Underline"
              >
                <u>U</u>
              </button>
              <button
                onClick={() => tiptapEditor.chain().focus().toggleStrike().run()}
                className={tiptapEditor.isActive('strike') ? 'is-active' : ''}
                title="Strike"
              >
                <s>S</s>
              </button>
              <div className="toolbar-divider"></div>
              <button
                onClick={() => tiptapEditor.chain().focus().toggleHeading({ level: 1 }).run()}
                className={tiptapEditor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
                title="Heading 1"
              >
                H1
              </button>
              <button
                onClick={() => tiptapEditor.chain().focus().toggleHeading({ level: 2 }).run()}
                className={tiptapEditor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
                title="Heading 2"
              >
                H2
              </button>
              <button
                onClick={() => tiptapEditor.chain().focus().toggleHeading({ level: 3 }).run()}
                className={tiptapEditor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
                title="Heading 3"
              >
                H3
              </button>
              <div className="toolbar-divider"></div>
              <button
                onClick={() => tiptapEditor.chain().focus().toggleBulletList().run()}
                className={tiptapEditor.isActive('bulletList') ? 'is-active' : ''}
                title="Bullet List"
              >
                •
              </button>
              <button
                onClick={() => tiptapEditor.chain().focus().toggleOrderedList().run()}
                className={tiptapEditor.isActive('orderedList') ? 'is-active' : ''}
                title="Numbered List"
              >
                1.
              </button>
              <button
                onClick={() => tiptapEditor.chain().focus().toggleBlockquote().run()}
                className={tiptapEditor.isActive('blockquote') ? 'is-active' : ''}
                title="Quote"
              >
                "
              </button>
              <div className="toolbar-divider"></div>
              <button
                onClick={() => tiptapEditor.chain().focus().setTextAlign('left').run()}
                className={tiptapEditor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
                title="Align Left"
              >
                ←
              </button>
              <button
                onClick={() => tiptapEditor.chain().focus().setTextAlign('center').run()}
                className={tiptapEditor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
                title="Align Center"
              >
                ↔
              </button>
              <button
                onClick={() => tiptapEditor.chain().focus().setTextAlign('right').run()}
                className={tiptapEditor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
                title="Align Right"
              >
                →
              </button>
              <div className="toolbar-divider"></div>
              <button
                onClick={() => {
                  const url = window.prompt('Enter URL');
                  if (url) {
                    tiptapEditor.chain().focus().setLink({ href: url }).run();
                  }
                }}
                className={tiptapEditor.isActive('link') ? 'is-active' : ''}
                title="Insert Link"
              >
                🔗
              </button>
              <button
                onClick={() => {
                  const url = window.prompt('Enter image URL');
                  if (url) {
                    tiptapEditor.chain().focus().setImage({ src: url }).run();
                  }
                }}
                title="Insert Image"
              >
                🖼️
              </button>
              <button
                onClick={() => tiptapEditor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                title="Insert Table"
              >
                📊
              </button>
            </div>
            <EditorContent editor={tiptapEditor} />
          </div>
        );

             

      default: // React Quill
        return (
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={onChange}
            modules={quillModules}
            formats={quillFormats}
            placeholder={placeholder}
            style={{ height }}
          />
        );
    }
  };

  return (
    <div className="enhanced-rich-text-editor">
      {/* Import HTML Section */}
      <div className="import-html-section">
        <div className="import-content">
          <label htmlFor="html-file-input">
            <span className="import-icon">📄</span>
            <span className="import-text">Import HTML</span>
          </label>
          <input
            ref={fileInputRef}
            id="html-file-input"
            type="file"
            accept=".html,.htm"
            onChange={handleFileImport}
          />
          <span className="import-description">
            Select an HTML file to import content
          </span>
          {importStatus && (
            <div className={`import-status ${importStatus.includes('Error') ? 'error' : 'success'}`}>
              {importStatus}
            </div>
          )}
        </div>
      </div>

      <div className="editor-type-selector">
        <label>Editor Type: </label>
                 <select
           value={editorTypeState}
           onChange={(e) => setEditorTypeState(e.target.value)}
           className="editor-select"
         >
           <option value="quill">React Quill (Advanced)</option>
           <option value="tiptap">TipTap (Modern)</option>
         </select>
      </div>
      <div className="editor-container">
        {renderEditor()}
      </div>
    </div>
  );
};

export default EnhancedRichTextEditor; 